import React, { useEffect, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useOktaAuth } from '@okta/okta-react';

import ReactGA from 'react-ga4';
import AdSense from 'react-adsense';
import { 
  FacebookShareButton, 
  LinkedinShareButton, 
  TelegramShareButton, 
  TwitterShareButton,
  FacebookIcon,
  LinkedinIcon,
  TelegramIcon,
  TwitterIcon
} from 'react-share';

import CookieConsent from "react-cookie-consent";

import { fetchBeers, fetchMoreBeers } from "../actions/beerActions";
import Search from "./search";
import Beers from "./beers";

const Home = ({ beers, page, isLoading, error, searchQuery, fetchBeers, fetchMoreBeers }) => {
  const { authState } = useOktaAuth();
  const [attempts, setAttempts] = useState(0);
  const [isGivingUp, setIsGivingUp] = useState(false);

  useEffect(() => {
    // load beers if none are found in state
    if (beers && beers.length === 0) {
      fetchBeers(page);
    }
  }, [beers, fetchBeers, page, isGivingUp]);

  useEffect(() => {
    // Reset retry logic if data is successfully loaded
    if (beers && beers.length > 0 && (attempts > 0 || isGivingUp)) {
      setAttempts(0);
      setIsGivingUp(false);
    }
  }, [beers, attempts, isGivingUp]);

  useEffect(() => {
    // If there is an error and we haven't exhausted retries, start a 30s timer
    if (error && attempts < 5 && !isLoading) {
      const timer = setTimeout(() => {
        console.info(`Retry attempt ${attempts + 1} for health data sync...`);
        setAttempts(prev => prev + 1);
        fetchBeers(page);
      }, 30000);

      return () => clearTimeout(timer);
    }

    // If we reached 5 attempts and still have an error, give up
    if (error && attempts >= 5) {
      setIsGivingUp(true);
    }
  }, [error, attempts, isLoading, fetchBeers, page]);

  const onScroll = useCallback(() => {
    // exit function if beers are currently being loaded
    if (isLoading) return;

    // check if scroll is at the bottom of the page
    if (
      window.innerHeight + document.documentElement.scrollTop >
      document.documentElement.offsetHeight - 100
    ) {
      // load more beers and append to state
      fetchMoreBeers(page);
    }
  }, [isLoading, fetchMoreBeers, page]);

  useEffect(() => {
    window.addEventListener("scroll", onScroll, false);
    return () => window.removeEventListener("scroll", onScroll, false);
  }, [onScroll]);

  return (
    <>
      <CookieConsent
        location="bottom"
        buttonText="I understand and Agree"
        cookieName="CD12_CookieAcceptv12022"
        style={{ background: "#2B373B", opacity: "0.8" }}
        buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
        expires={150}
      >
        This website uses cookies and data to enhance the user experience. By clicking 'I understand and Agree', you agree to terms, described in:{" "}
        <span style={{ fontSize: "10px" }}><a href="https://linardsliepins.files.wordpress.com/2021/03/confidentiality-policy-pdf-v1y2021.pdf#view=fitH">Privacy Policy</a>&nbsp;|&nbsp;<a href="https://linardsliepins.files.wordpress.com/2021/03/confidentiality-policy-pdf-v1y2021.pdf#view=fitH">Cookie Policy / ToC</a></span>
      </CookieConsent>
      <header className="justify-content-center py-2 bg-warning text-center text-white">
        <div className="container">
          <div className="row">
            <div className="col">
              <h1 className="font-weight-bold">Beer Bank | {authState?.isAuthenticated ? 'The Personalized Cloud Dashboard' : 'The Cloud Dashboard'} {process.env.REACT_APP_VERSION}</h1>
              <p>Find your favourite beer here (or <i>open</i> your <u>personalized dashboard</u> to access your available services)</p>
            </div>
          </div>
          <Search />
          <div className="d-flex justify-content-center gap-2 mt-3">
            <TwitterShareButton url={window.location.href}><TwitterIcon size={32} round /></TwitterShareButton>
            <FacebookShareButton url={window.location.href}><FacebookIcon size={32} round /></FacebookShareButton>
            <TelegramShareButton url={window.location.href}><TelegramIcon size={32} round /></TelegramShareButton>
            <LinkedinShareButton url={window.location.href}><LinkedinIcon size={32} round /></LinkedinShareButton>
          </div>
        </div>
      </header>
      <Beers beers={beers} isLoading={isLoading} error={error} searchQuery={searchQuery} isGivingUp={isGivingUp} retryCount={attempts} />
      <AdSense.Google
        client={process.env.REACT_APP_GADS_ID || 'ca-pub-2835578352930332'}
        slot='7806394673'
        style={{ display: 'block' }}
        format='auto'
        responsive='true'
      />
    </>
  );
};

// redux stuff
Home.propTypes = {
  fetchBeers: PropTypes.func.isRequired,
  fetchMoreBeers: PropTypes.func.isRequired,
  beers: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  searchQuery: PropTypes.string
};

const mapStateToProps = state => ({
  beers: state.beer.beers,
  page: state.beer.page,
  isLoading: state.beer.isLoading,
  error: state.beer.error,
  searchQuery: state.beer.searchQuery
});

export default connect(
  mapStateToProps,
  { fetchBeers, fetchMoreBeers }
)(Home);
